/**
 * @param {string | number} datetime ISO string or Unix Timestamp
 * @param {object} [i18n] i18n config options
 * @param {string} [i18n.locale='en-US'] BCP 47 language tag
 * @param {string} [i18n.timeZone='UTC'] IANA time zone string. If provided, forces a timezone format.
 * @return {string|undefined} Date time in "June 20, 2022, 1:30 PM (UTC)" format
 */
const getFormattedDateTime = (datetime, i18n = {}) => {
    if (!datetime) {
        return undefined;
    }

    const locale = i18n.locale || 'en-US';
    const timeZone = i18n.timeZone || 'UTC';

    const date = new Date(datetime);

    const dateFormatter = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone,
    });

    const timeFormatter = new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: 'numeric',
        timeZoneName: 'short',
        timeZone,
    });

    const timeZoneName = timeFormatter
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName').value;
    const formattedDate = dateFormatter.format(date);
    const formattedTime = timeFormatter.format(date).replace(timeZoneName, `(${timeZoneName})`);

    return `${formattedDate}, ${formattedTime}`;
};

/**
 * Convert locale to support BCP 47 language tag for international date time formatting
 * @param {object} [i18n] i18n config options
 * @param {string} [i18n.language] users language
 * @param {string} [i18n.locale] users locale
 * @param {string} [i18n.timeZone] users timezone
 * @return {object} new formatted i18N object
 */
export const getI18nOptionsForDateFormatting = (i18n) => {
    const i18nOptions = { ...i18n };
    if (i18n.locale) {
        i18nOptions.locale = i18n.locale.replace('_', '-');
    }
    return i18nOptions;
};

export default getFormattedDateTime;
